import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon, {
  AssetTypes,
} from "../../../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import PathPickOrderDetailCard from "@/features/pathpick/features/order-history/features/detail/components/PathPickOrderDetailCard";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrderInvoice } from "@/features/pathpick/features/order-history/types/response";
import { formatPathPickOrderDate } from "@/features/pathpick/features/order-history/utils";

interface Props {
  invoice?: PathPickOrderInvoice | null;
}

// 확장자별 파일 아이콘. 여기 없는 확장자(png 등)와 확장자 없는 파일명은 기본 file 아이콘으로 떨어진다.
const FILE_ICON_BY_EXTENSION: Record<string, AssetTypes> = {
  pdf: "file-pdf",
  xls: "file-xls",
  xlsx: "file-xlsx",
  csv: "file-csv",
  doc: "file-doc",
  docx: "file-docx",
  ppt: "file-ppt",
  pptx: "file-pptx",
  txt: "file-txt",
  jpg: "file-jpg",
  jpeg: "file-jpeg",
};

const resolveFileIcon = (fileName: string): AssetTypes =>
  FILE_ICON_BY_EXTENSION[fileName.split(".").pop()?.toLowerCase() ?? ""] ??
  "file";

// 거래명세서는 주문 단위 문서로, 관리자가 등록하기 전에는 응답에 없다.
// 등록 전에도 카드는 남겨 안내 문구와 비활성 버튼을 보여준다.
function PathPickOrderDetailInvoice({ invoice }: Props) {
  const { t } = useI18nHelper();
  // 파일은 uno_file id로만 내려오므로 프리사인 다운로드를 거친다(정산 내역 신용 파일과 동일한 방식).
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();

  const handleDownload = () => {
    if (!invoice) return;
    fireDownloadUnoFile({ unoFileId: invoice.fileId }, {});
  };

  return (
    <PathPickOrderDetailCard
      title={t(`${ORDER_DETAIL_KEY}invoice_title`)}
      gap={"12px"}
    >
      {invoice ? (
        <Flex gap={"8px"} alignItems={"center"}>
          <CustomIcon
            src={resolveFileIcon(invoice.name)}
            width={24}
            height={24}
          />
          <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {invoice.name}
            </Text>
            {/* 등록일은 날짜만 내려와 시각은 표기하지 않는다(키의 datetime 자리에 날짜를 넣는다). */}
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {t(`${ORDER_DETAIL_KEY}invoice_registered_at`, {
                datetime: formatPathPickOrderDate(invoice.issuedOn),
              })}
            </Text>
          </Flex>
        </Flex>
      ) : (
        <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
          {t(`${ORDER_DETAIL_KEY}invoice_placeholder`)}
        </Text>
      )}
      <AppButton
        variant={"white"}
        isDisabled={!invoice}
        onClick={handleDownload}
      >
        <Flex gap={"4px"} alignItems={"center"}>
          <CustomIcon
            src={"download"}
            width={16}
            height={16}
            color={invoice ? "#1D1D1B" : "#8F8F8C"}
          />
          <Text textStyle={"body-sm-bold"}>
            {t(`${ORDER_DETAIL_KEY}invoice_download`)}
          </Text>
        </Flex>
      </AppButton>
    </PathPickOrderDetailCard>
  );
}

export default PathPickOrderDetailInvoice;
